import * as THREE from 'three';
import {
  all, chain, map, range, sortBy
} from 'ramda';

export type CubePieceKind = 'face' | 'edge' | 'corner';

export interface CubePiece {
  key: string;
  kind: CubePieceKind;
  direction: THREE.Vector3;
  geometry: THREE.BufferGeometry;
  label?: string;
}

export interface CompassPoint {
  label: string;
  direction: THREE.Vector3;
}

export interface Pose {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
}

type Signs = [number, number, number];

interface CubeVertex {
  axis: number;
  signs: Signs;
  position: THREE.Vector3;
}

const CUBE_HALF = 0.5;
const CHAMFER = 0.14;
export const RING_INNER = 1.02;
export const RING_OUTER = 1.3;
export const RING_MID = (RING_INNER + RING_OUTER) / 2;
const RING_THICKNESS = 0.05;
export const LETTER_SIZE = 0.26;

const INSET = CUBE_HALF - CHAMFER;
const LETTER_LIFT = 0.002;
const RING_SEGMENTS = 96;
const AXES = [0, 1, 2];
const WORLD_UP = new THREE.Vector3(0, 1, 0);

const KIND_BY_AXIS_COUNT: Record<number, CubePieceKind> = { 1: 'face', 2: 'edge', 3: 'corner' };

// Three.js +Z is north and -X is east (see cadToThreeJsPosition), so FRONT is the south face, as in Deswik.
const FACE_LABELS: Record<string, string> = {
  '0,0,-1': 'FRONT',
  '0,0,1': 'BACK',
  '-1,0,0': 'RIGHT',
  '1,0,0': 'LEFT',
  '0,1,0': 'TOP',
  '0,-1,0': 'BOTTOM',
};

export const COMPASS_POINTS: CompassPoint[] = [
  { label: 'N', direction: new THREE.Vector3(0, 0, 1) },
  { label: 'E', direction: new THREE.Vector3(-1, 0, 0) },
  { label: 'S', direction: new THREE.Vector3(0, 0, -1) },
  { label: 'W', direction: new THREE.Vector3(1, 0, 0) },
];

const cartesian = (values: number[]): Signs[] => chain(
  (x) => chain((y) => map((z): Signs => [x, y, z], values), values),
  values,
);

const signsKey = (signs: Signs) => signs.join(',');

const CUBE_VERTICES: CubeVertex[] = chain(
  (axis) => map((signs) => ({
    axis,
    signs,
    position: new THREE.Vector3().fromArray(map((i) => signs[i] * (i === axis ? CUBE_HALF : INSET), AXES)),
  }), cartesian([-1, 1])),
  AXES,
);

const belongsToPiece = (piece: Signs) => ({ axis, signs }: CubeVertex) => piece[axis] !== 0
  && all((i) => piece[i] === 0 || piece[i] === signs[i], AXES);

const perpendicularTo = (direction: THREE.Vector3) => {
  const reference = Math.abs(direction.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
  return reference.sub(direction.clone().multiplyScalar(direction.dot(reference))).normalize();
};

const sortCounterClockwise = (direction: THREE.Vector3, points: THREE.Vector3[]): THREE.Vector3[] => {
  const centre = points.reduce((sum, point) => sum.add(point), new THREE.Vector3()).divideScalar(points.length);
  const first = perpendicularTo(direction);
  const second = direction.clone().cross(first);
  return sortBy((point) => {
    const offset = point.clone().sub(centre);
    return Math.atan2(offset.dot(second), offset.dot(first));
  }, points);
};

const faceTextBasis = (normal: THREE.Vector3) => {
  const up = Math.abs(normal.y) > 0.5 ? new THREE.Vector3(0, 0, Math.sign(normal.y)) : WORLD_UP.clone();
  return { up, right: up.clone().cross(normal) };
};

const buildPolygonGeometry = (direction: THREE.Vector3, outline: THREE.Vector3[], withUvs: boolean) => {
  const { up, right } = faceTextBasis(direction);
  const triangles = chain((i) => [outline[0], outline[i], outline[i + 1]], range(1, outline.length - 1));
  const uvFor = (point: THREE.Vector3) => (withUvs
    ? [point.dot(right) / (2 * INSET) + 0.5, point.dot(up) / (2 * INSET) + 0.5]
    : [0, 0]);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(chain((point) => point.toArray(), triangles), 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(chain(uvFor, triangles), 2));
  geometry.computeVertexNormals();
  return geometry;
};

const buildPiece = (signs: Signs): CubePiece => {
  const direction = new THREE.Vector3().fromArray(signs).normalize();
  const kind = KIND_BY_AXIS_COUNT[signs.filter((sign) => sign !== 0).length];
  const outline = sortCounterClockwise(direction, map(({ position }) => position, CUBE_VERTICES.filter(belongsToPiece(signs))));
  return {
    key: signsKey(signs),
    kind,
    direction,
    geometry: buildPolygonGeometry(direction, outline, kind === 'face'),
    label: FACE_LABELS[signsKey(signs)],
  };
};

export const buildCubePieces = (): CubePiece[] => map(buildPiece, cartesian([-1, 0, 1]).filter((signs) => signs.some((sign) => sign !== 0)));

export const buildRingGeometry = (): THREE.BufferGeometry => {
  const shape = new THREE.Shape().absarc(0, 0, RING_OUTER, 0, Math.PI * 2, false);
  shape.holes.push(new THREE.Path().absarc(0, 0, RING_INNER, 0, Math.PI * 2, true));
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: RING_THICKNESS, bevelEnabled: false, curveSegments: RING_SEGMENTS });
  geometry.rotateX(Math.PI / 2);
  geometry.translate(0, -CUBE_HALF, 0);
  return geometry;
};

export const compassLetterPose = (direction: THREE.Vector3): Pose => ({
  position: direction.clone().multiplyScalar(RING_MID).setY(-CUBE_HALF + LETTER_LIFT),
  quaternion: new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(direction.clone().cross(WORLD_UP), direction, WORLD_UP),
  ),
});
